import Grid2 from '@mui/material/Grid2';
import { useEffect, useState } from 'react';
import { color } from '@/colors';
import { DashboardCard } from './DashboardCard';
import { DashboardInlineEditInput } from './DashboardInlineEditInput';
import { FolderCardDisplay } from './components/FolderCardDisplay';
import type { FolderResponseDto } from '@/types/folder.types';

interface FolderCardProps {
  folder: FolderResponseDto;
  onFolderNavigate?: (folderId?: string | null) => void;
  handleContextMenu?: (event: React.MouseEvent<HTMLDivElement>, id: string, itemType?: 'folder' | 'recipe') => void;
  workflowPosters?: string[];
  workflowCount?: number;
  isEditing?: boolean;
  onRename?: (id: string, name: string) => void;
  onCancelRename?: (id: string) => void;
  isDragOver?: boolean;
  isDragging?: boolean;
  onDragOver?: (e: React.DragEvent, folderId: string) => void;
  onDragLeave?: () => void;
  onDrop?: (e: React.DragEvent, folderId: string) => void;
  onDragStart?: (e: React.DragEvent, itemId: string) => void;
  onDragEnd?: () => void;
}

export const FolderCard = ({
  folder,
  onFolderNavigate,
  handleContextMenu,
  workflowPosters = [],
  workflowCount = 0,
  isEditing = false,
  onRename,
  onCancelRename,
  isDragOver = false,
  isDragging = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
}: FolderCardProps) => {
  const [editingValue, setEditingValue] = useState(folder.name);
  const totalFiles = workflowCount || 0;

  useEffect(() => {
    if (isEditing) {
      setEditingValue(folder.name);
    }
  }, [isEditing, folder.name]);

  const handleClick = () => {
    if (onFolderNavigate && !isEditing) {
      onFolderNavigate(folder.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editingValue.trim() && onRename) {
        onRename(folder.id, editingValue);
      } else if (onCancelRename) {
        onCancelRename(folder.id);
      }
    } else if (e.key === 'Escape' && onCancelRename) {
      e.preventDefault();
      onCancelRename(folder.id);
    }
  };

  const handleRightClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleContextMenu) {
      handleContextMenu(e, folder.id, 'folder');
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
      <DashboardCard
        data-testid={`folder-card-${folder.id}`}
        draggable={!isEditing}
        onDragStart={(e) => onDragStart?.(e, folder.id)}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver?.(e, folder.id);
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          onDrop?.(e, folder.id);
        }}
        sx={{
          cursor: isEditing ? 'default' : isDragging ? 'grabbing' : 'pointer',
          border: 'none',
        }}
        onClick={handleClick}
        onContextMenu={handleRightClick}
      >
        <FolderCardDisplay
          id={folder.id}
          name={folder.name}
          posters={workflowPosters}
          totalFiles={totalFiles}
          thumbnailSx={isDragOver ? { borderColor: color.White40_T } : undefined}
          nameContent={
            <DashboardInlineEditInput
              isEditing={isEditing}
              value={editingValue}
              displayValue={folder.name}
              onChange={setEditingValue}
              onKeyDown={handleKeyDown}
              onBlur={() => onCancelRename?.(folder.id)}
            />
          }
        />
      </DashboardCard>
    </Grid2>
  );
};
